import { ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ImageConfiguration } from '../../../models/image-configuration';

// Same policy as BusinessImageDirective: three attempts, retried after 1s then 3s, 15s each.
const RETRY_DELAYS = [1000, 3000];
const ATTEMPT_TIMEOUT = 15000;

@Component({
  selector: 'app-dashboard-slider',
  templateUrl: './dashboard-slider.component.html',
  styleUrls: ['./dashboard-slider.component.scss']
})
export class DashboardSliderComponent implements OnInit, OnDestroy {
  @ViewChild('canvas', { static: true }) canvas!: ElementRef<HTMLCanvasElement>;
  @Input() slideNumber: number;
  @Input() imageSrc: string;
  @Input() title: string;
  @Input() info: string;
  @Input() resturantId: number;
  @Input() imgMetaData: any;

  imageFailed = false;
  private intersectionObserver?: IntersectionObserver;
  private hasDrawn = false;
  private image?: HTMLImageElement; // Kept once loaded, so a resize redraws without refetching.
  private request?: HTMLImageElement;
  private timer?: ReturnType<typeof setTimeout>;
  private attempts = 0;

  constructor(private host: ElementRef<HTMLElement>, private changeDetector: ChangeDetectorRef) {}

  ngOnInit(): void {
    // Only the visible slide paints immediately. The rest wait until keen-slider
    // brings them near the viewport — opening the dashboard used to download and
    // canvas-draw every hero image at once.
    if (this.slideNumber === 1 || typeof IntersectionObserver === 'undefined') {
      this.cropImage();
      return;
    }
    this.intersectionObserver = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        this.intersectionObserver?.disconnect();
        this.intersectionObserver = undefined;
        this.cropImage();
      }
    }, { root: this.host.nativeElement.closest('.keen-slider'), rootMargin: '0px 100% 0px 100%' });
    // Keen translates the inner slide, while Angular's component host stays put.
    // Observe that moving element and prepare its neighbours before they enter.
    this.intersectionObserver.observe(this.host.nativeElement.querySelector('.keen-slider__slide') || this.host.nativeElement);
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
    clearTimeout(this.timer);
    this.timer = undefined;
    if (this.request) {
      this.request.onload = this.request.onerror = null;
      this.request.removeAttribute('src');
      this.request = undefined;
    }
  }

  cropImage() {
    if (!this.imgMetaData) return;
    if (this.image) {
      this.draw(this.image);
      return;
    }
    // A load or retry is already pending, or every attempt failed and the fallback is showing.
    if (this.request || this.timer || this.imageFailed) return;
    this.hasDrawn = true;
    this.load();
  }

  private load(): void {
    const img = new Image();
    this.request = img;
    this.attempts++;
    const settle = (loaded: boolean) => {
      if (this.request !== img) return;
      clearTimeout(this.timer);
      this.timer = undefined;
      img.onload = img.onerror = null;
      this.request = undefined;
      if (loaded) {
        this.image = img;
        this.draw(img);
      } else if (this.attempts <= RETRY_DELAYS.length) {
        img.removeAttribute('src'); // Abandon a download that hit the timeout.
        this.timer = setTimeout(() => {
          this.timer = undefined;
          this.load();
        }, RETRY_DELAYS[this.attempts - 1]);
      } else {
        img.removeAttribute('src');
        this.imageFailed = true;
        this.changeDetector.detectChanges();
      }
    };
    img.onload = () => settle(true);
    img.onerror = () => settle(false);
    this.timer = setTimeout(() => settle(false), ATTEMPT_TIMEOUT);
    img.crossOrigin = 'anonymous';
    img.src = this.imageSrc;
  }

  private draw(img: HTMLImageElement): void {
    const canvas = this.canvas.nativeElement;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.log("ctx error");
      return;
    }

    canvas.width = window.innerWidth;
    canvas.height = this.imgMetaData.imageType == 1 ? 532 : 268;

    ctx.drawImage(
      img,
      this.imgMetaData.x, this.imgMetaData.y,
      this.imgMetaData.width, this.imgMetaData.height,
      0, 0,
      window.innerWidth, canvas.height
    );
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: any) {
    // Redraw only slides that have painted; a resize must not force-load the
    // images the observer is still deferring.
    if (this.hasDrawn) {
      this.cropImage();
    }
  }
}
